import { config } from 'config';
import { documentTypeOptions } from 'shared/fixtures';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { optionActions } from 'core/Option';
import { patientActions } from 'core/Patient';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AddressDetailsContainer,
    IconContainer,
    ResponsiveCol,
    Text
} from './styled';
import {
    AreaPicker,
    CompanyPicker,
    CostCenterPicker,
    CoveragePicker,
    DoctorPicker,
    GenderPicker,
    PatientPicker,
    PlanPicker,
    PositionPicker,
    WorkplacePicker
} from 'views/pickers';
import {
    Avatar,
    ButtonFooter,
    Checkbox,
    Container,
    DateSelect,
    DragAndDrop,
    ExpandButton,
    FormGroup,
    IconButton,
    Input,
    MaterialIcon,
    SelectSearch,
    TagList
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import {
    ConfirmationModal,
    ErrorMessage,
    FormLayout,
    PasswordSecurityCheck,
    PhoneInput,
    PositionModal
} from 'views/components';
import React, { useEffect, useState } from 'react';
import {
    checkPasswordSecurity,
    dateToObject,
    formatOptions,
    isAlphanumeric,
    objectToDate,
    parsePatientInChargeRelation,
    parseStringSelect,
    validationSchema
} from 'shared/utils';
import {
    useArray,
    useCheckbox,
    useDispatch,
    useErrors,
    useFileUpload,
    useInput,
    useSelector
} from 'hooks';
const {
    allowPatientChangePassword,
    areas,
    company,
    costCenters,
    coverages: coveragesConfig,
    defaultPhone,
    doctorsRelated: doctorsRelatedConfig,
    patientIdentificationAlpha,
    patientMinimumFields,
    patientProfile,
    positions,
    recipes,
    ssoLogin,
    workplaces
} = config;

const PatientRegistration = () => {
    
    const dispatch = useDispatch();

    const { configurations } = useSelector('config');
    const strongPasswordRequired = configurations?.strong_password_required === '1';
    const {
        cities,
        citiesLoading,
        districts,
        districtsLoading,
        provinces,
        provincesLoading
    } = useSelector('option');
    const {
        loading,
        positionsSucceded,
        registrationError,
        selected: selectedData
    } = useSelector('patient');
    
    const { id } = useParams();
    const { t } = useTranslation();
    const editId = id ? Number(id) : null;
    const editMode = !!id;
    const controlIdentification = value => !patientIdentificationAlpha || !value || isAlphanumeric(value[value.length - 1]);

    const [uploadAvatar, uploadingAvatar] = useFileUpload();
    
    const [acceptConfirm, setAcceptConfirm] = useState(false);
    const [addressInput, address, setAddress] = useInput('', {});
    const [areaInput, areaValue, setAreaValue] = useInput(null);
    const [avatarId, setAvatarId] = useState(undefined);
    const [avatarUrl, setAvatarUrl] = useState(undefined);
    const [birthDate, setBirthDate] = useState(null);
    const [buildingNumberInput, buildingNumber, setBuildingNumber] = useInput(
        '',
        { type: 'number' }
    );
    const [cellphoneNumber, setCellphoneNumber] = useState('+54');
    const [companyInput, companyValue, setCompanyValue] = useInput(null);
    const [cityIdInput, cityId, setCityId] = useInput(null, {});
    const [costCenterInput, costCenterValue, setCostCenterValue] = useInput(null);
    const [districtIdInput, districtId, setDistrictId] = useInput('', {});
    const [doorNumberInput, doorNumber, setDoorNumber] = useInput('', {});
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [doctor, setDoctor] = useState({ id: 0 });
    const [doctorsRelated, setDoctorsRelated, {
        flatBy: flatDoctorsRelated,
        push: pushDoctor,
        remove: removeDoctor
    }] = useArray([], { type: 'object' });
    const [emailInput, email, setEmail] = useInput(
        '',
        { type: 'mail' },
        () => {
            clearError('email');
            handleClear('email');
        }
    );
    const {
        clearError,
        clearErrors,
        errors,
        setErrors
    } = useErrors();
    const [enabledCheckBox, enabled, setEnabled] = useCheckbox(true);
    const [firstNameInput, firstName, setFirstName] = useInput('', {}, () => clearError('firstName'));
    const [floorInput, floor, setFloor] = useInput('', {});
    const [gender, setGender] = useState('Male');
    const [healthInsuranceInput, healthInsurance, setHealthInsurance] = useInput('', {});
    const [identificationInput, identification, setIdentification] = useInput(
        '',
        { type: patientIdentificationAlpha ? 'text' : 'number' },
        () => {
            clearError('identification');
            handleClear('identification');
        },
        controlIdentification
    );
    const [identificationTypeInput, identificationType, setIdentificationType] = useInput(1);
    const [lastNameInput, lastName, setLastName] = useInput('', {}, () => clearError('lastName'));
    const [medicalCoverageIdInput, medicalCoverageId, setMedicalCoverageId] = useInput(null, {});
    const [mandatoryPasswordChangeChekBox, mandatoryPasswordChange, setMandatoryPasswordChange] = useCheckbox(true);
    const [patientInChargeIdentification, setPatientInChargeIdentification] = useState('');
    const [patientInChargeInput, patientInCharge, setPatientInCharge] = useInput(null);
    const [patientInChargeRelation, setPatientInChargeRelation] = useState('parent');
    const [passwordInput, password, setPassword] = useInput('', { type: 'password' }, () => clearError('password'));
    const [positionInput, positionValue, setPositionValue] = useInput(null);
    const [planIdInput, planId, setPlanId] = useInput(null, {});
    const [positionModal, setPositionModal] = useState(false);
    const [provinceIdInput, provinceId, setProvinceId] = useInput(null, {});
    const [repeatPasswordInput, repeatPassword, setRepeatPassword] = useInput('', { type: 'password' }, () => clearError('password'));
    const [showAddressDetails, setShowAddressDetails] = useState(editMode);
    const [workplaceInput, workplaceValue, setWorkplaceValue] = useInput(null);

    useEffect(() => {
        dispatch(optionActions.getProvinces());
    }, []);

    useEffect(() => {
        if (!isEmpty(registrationError)) {
            setAcceptConfirm(false);
        }
    }, [registrationError]);

    useEffect(() => {
        clearErrors();
        setPassword('');
        setRepeatPassword('');
        if (editId) {
            dispatch(patientActions.getPatientData(editId));
        } else {
            setAddress('');
            setAreaValue(null);
            setBirthDate(null);
            setBuildingNumber('');
            setCellphoneNumber('');
            setCityId(null);
            setCostCenterValue(null);
            setDistrictId('');
            setDoorNumber('');
            setDoctorsRelated([]);
            setEnabled(true);
            setEmail('');
            setFirstName('');
            setFloor('');
            setHealthInsurance('');
            setIdentification('');
            setIdentificationType(1);
            setLastName('');
            setMedicalCoverageId(null);
            setMandatoryPasswordChange(allowPatientChangePassword);
            setPatientInCharge(null);
            setPatientInChargeIdentification('');
            setPatientInChargeRelation('parent');
            setPositionValue(null);
            setPlanId(null);
            setProvinceId(null);
            setWorkplaceValue(null);
        }
    }, [editId]);

    useEffect(() => {
        if (!!editId) {
            const {
                address = '',
                area_id = null,
                avatar,
                birth_date,
                building_number = '',
                cellphone_number = '',
                city_id,
                company,
                cost_center_id = null,
                deleted,
                district_id,
                door_number = '',
                doctors_related = [],
                email = '',
                enabled = true,
                first_name = '',
                floor = '',
                gender,
                health_insurance = '',
                identification = '',
                identification_id = 1,
                last_name = '',
                mandatory_password_change = false,
                medical_coverage,
                patient_in_charge,
                plan,
                position_id = null,
                province_id = '',
                workplace_id = null
            } = selectedData;
            setAddress(address ?? '');
            setAreaValue(area_id);
            setAvatarId(undefined);
            setAvatarUrl(avatar?.url);
            setBirthDate(!!birth_date ? moment(birth_date) : null);
            setBuildingNumber(building_number ?? '');
            setCellphoneNumber(cellphone_number);
            setCityId(city_id);
            setCompanyValue(company?.id ?? null);
            setCostCenterValue(cost_center_id);
            setDeleted(deleted);
            setDistrictId(district_id);
            setDoorNumber(door_number ?? '');
            setDoctorsRelated(doctors_related.map(
                ({ first_name, id, last_name }) => ({ label: `${ last_name }, ${ first_name }`, value: id })
            ));
            setEnabled(!!enabled);
            setEmail(email);
            setFirstName(first_name);
            setFloor(floor ?? '');
            setGender(!!gender ? gender : 'Male');
            setHealthInsurance(health_insurance ?? '');
            setIdentification(identification);
            setIdentificationType(identification_id);
            setLastName(last_name);
            setMedicalCoverageId(medical_coverage?.id ?? null);
            setMandatoryPasswordChange(!!mandatory_password_change);
            setPatientInCharge(patient_in_charge?.id);
            setPatientInChargeIdentification(patient_in_charge?.identification ?? '');
            setPatientInChargeRelation(patient_in_charge?.relation ?? 'parent');
            setPlanId(plan?.id ?? null);
            setPositionValue(position_id);
            setProvinceId(province_id);
            setWorkplaceValue(workplace_id);
        }
    }, [selectedData]);

    useEffect(() => {
        if (!!provinceId) {
            dispatch(optionActions.getCities(provinceId));
        }
        dispatch(optionActions.setOptionDistricts(null));
        setDistrictId(null);
    }, [provinceId]);

    useEffect(() => {
        if (!!cityId) {
            dispatch(optionActions.getDistricts(cityId));
        }
    }, [cityId]);

    useEffect(() => {
        if (!positionsSucceded) return;
        dispatch(patientActions.setPatientPositionsSucceded(false));
        setPositionModal(false);
    }, [positionsSucceded]);

    const handleAvatarUpload = async newFile => {
        const { data, success } = await uploadAvatar(newFile);
        if (!success) return;
        setAvatarId(data?.id);
        setAvatarUrl(data?.url);
    };

    const handleDoctorAdd = () => {
        const { id, name } = doctor;
        clearError('doctorsRelated');
        pushDoctor({
            label: name,
            value: id
        });
        setDoctor({ id: 0 });
    };

    const handleClear = key => {
        if (!!registrationError[key]) {
            const newErrors = { ...registrationError };
            delete newErrors[key];
            dispatch(patientActions.setPatientRegistrationError(newErrors));
        }
    };

    const onDeleteConfirm = value => {
        if (value === 'confirm') {
            dispatch(patientActions.deletePatient(editId));
        } else {
            setDeleteConfirm(false);
        }
    };

    const onSubmit = confirm => {
        if (confirm === 'confirm' && validate()) {
            const newPatient = {
                address,
                ...(avatarId && { avatar_id: avatarId }),
                ...(areas && { area_id: areaValue || null }),
                birth_date: moment(birthDate).isValid() ? moment(birthDate).format('YYYY-MM-DD') : null,
                building_number: buildingNumber,
                cellphone_number: cellphoneNumber,
                city_id: cityId ?? null,
                ...(company && { company_id: companyValue || null }),
                ...(costCenters && { cost_center_id: costCenterValue || null }),
                district_id: districtId ?? null,
                door_number: doorNumber,
                ...(doctorsRelatedConfig && { doctors_related: flatDoctorsRelated('value') }),
                email,
                enabled: enabled ? 1 : 0,
                first_name: firstName,
                floor,
                gender,
                health_insurance: healthInsurance,
                ...(!ssoLogin && {
                    identification,
                    identification_id: identificationType
                }),
                last_name: lastName,
                mandatory_password_change: mandatoryPasswordChange ? 1 : 0,
                medical_coverage_id: !!medicalCoverageId ? medicalCoverageId : 0,
                ...(editMode && !!password && !!repeatPassword && { password }),
                patient_in_charge_id: patientInCharge ?? null,
                patient_in_charge_relation: patientInCharge && patientInChargeRelation ? patientInChargeRelation : null,
                plan_id: !!planId ? planId : 0,
                ...(positions && { position_id: positionValue || null }),
                province_id: provinceId ?? null,
                ...(workplaces && { workplace_id: workplaceValue || null })
            };
            if (editMode) {
                const data = {
                    ...newPatient,
                    patient_id: editId
                };
                dispatch(patientActions.updatePatient(data));
            } else {
                dispatch(patientActions.createPatient(newPatient));
            }
        } else {
            setAcceptConfirm(false);
        }
    };

    const validate = () => {
        const { errors, isValid } = validationSchema({
            birthDate: [patientMinimumFields || moment(birthDate).isValid(), t('Requerido')],
            email: [patientMinimumFields || !!email, t('Requerido')],
            firstName: [!!firstName, t('Requerido')],
            identification: [ssoLogin || !!identification, t('Requerido')],
            lastName: [!!lastName, t('Requerido')],
            password: [
                [!editMode || (password === repeatPassword) || !repeatPassword, t('Las contraseñas deben coincidir')],
                [!strongPasswordRequired || !password || checkPasswordSecurity(password).isValid, t('La contraseña no es segura')]
            ]
        });
        setErrors(errors);
        return isValid;
    };

    const formTitle = `${ editMode ? t('Editar') : t('Añadir') } ${ t('paciente') }`;
    const confirmTitle = editMode ? t('¿Quieres editar el paciente ingresado?') : t('¿Quieres crear el paciente ingresado?');
    
    return (
        <>
            <ConfirmationModal
                actionHandler={ onSubmit }
                active={ acceptConfirm }
                loading={ loading }
                title={ confirmTitle }
            />
            <ConfirmationModal
                actionHandler={ onDeleteConfirm }
                active={ deleteConfirm }
                loading={ loading }
                title={ t('¿Quieres eliminar los datos del paciente?') }
            />
            <PositionModal
                active={ positionModal }
                patientId={ editId }
                onToggle={ () => setPositionModal(false) }
            />
            <FormLayout goBackTo={ `${ t('/pacientes') }${ editMode ? `${ t('/historial') }/${ editId }` : t('/listar') }` } title={ formTitle }>
                <Container>
                    <Row>
                        <Col xs="12">
                            <Avatar
                                alt={ t('Foto de perfil del paciente') }
                                size={ 120 }
                                src={ avatarUrl }
                            />
                            <FormGroup label={ t('Cambiar foto de perfil') } margin="10px 0px 10px 0px">
                                <DragAndDrop
                                    clearFileAfterChange
                                    loading={ uploadingAvatar }
                                    onFileChange={ handleAvatarUpload }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Apellido') }>
                                <Input
                                    error={ errors.lastName }
                                    { ...lastNameInput }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Nombre') }>
                                <Input
                                    error={ errors.firstName }
                                    { ...firstNameInput }
                                />
                            </FormGroup>
                        </Col>
                        {
                            !ssoLogin &&
                            <>
                                <Col md="6" sm="12">
                                    <FormGroup label={ t('Tipo de documento') }>
                                        <SelectSearch
                                            { ...identificationTypeInput }
                                            margin="0px 0px 20px 0px"
                                            options={ documentTypeOptions }
                                            width="100%"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm="6" xs="12">
                                    <FormGroup label={ t('Documento') }>
                                        <Input
                                            error={ errors.identification ?? registrationError.identification }
                                            { ...identificationInput }
                                        />
                                    </FormGroup>
                                </Col>
                            </>
                        }
                        <Col sm="6" xs="12">
                            <FormGroup label={ t('Email') }>
                                <Input
                                    error={ errors.email ?? registrationError.email }
                                    type="email"
                                    { ...emailInput }
                                />
                            </FormGroup>
                        </Col>
                        <Col sm="6" xs="12">
                            <FormGroup label={ t('Fecha de nacimiento') }>
                                <DateSelect
                                    error={ errors.birthDate }
                                    margin="0px 0px 20px 0px"
                                    value={ birthDate ? dateToObject(birthDate) : null }
                                    onChange={ value => setBirthDate(objectToDate(value)) }
                                />
                            </FormGroup>
                        </Col>
                        <Col sm="6" xs="12">
                            <FormGroup label={ t('Celular') }>
                                <PhoneInput
                                    margin="0px 0px 20px 0px"
                                    value={ cellphoneNumber ?? defaultPhone.code }
                                    onChange={ value => setCellphoneNumber(value) }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        {
                            !showAddressDetails &&
                                <AddressDetailsContainer>
                                    <IconContainer onClick={ setShowAddressDetails }>
                                        <MaterialIcon icon="add_circle_outline" size={ 22 } />
                                    </IconContainer>
                                    <Text>
                                        { t('Agregar datos de domicilio') }
                                    </Text>
                                </AddressDetailsContainer>
                        }
                        {
                            !!showAddressDetails &&
                                <>
                                    <Col md="6" xs="12">
                                        <FormGroup label={ t('Dirección') }>
                                            <Input
                                                placeholder={ t('Ingrese una dirección') }
                                                { ...addressInput }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="2" xs="12">
                                        <FormGroup label="N°">
                                            <Input
                                                { ...buildingNumberInput }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="2" xs="12">
                                        <FormGroup label={ t('Piso') }>
                                            <Input
                                                { ...floorInput }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="2" xs="12">
                                        <FormGroup label={ t('Puerta') }>
                                            <Input
                                                { ...doorNumberInput }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6" sm="12">
                                        <FormGroup label={ t('Provincia') }>
                                            <SelectSearch
                                                disabled={ !!provincesLoading }
                                                margin="0px 0px 20px 0px"
                                                options={ formatOptions(provinces) }
                                                placeholder={ !!provincesLoading ? `${ t('Cargando') }...` : t('Ingrese una provincia') }
                                                width="100%"
                                                { ...provinceIdInput }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6" sm="12">
                                        <FormGroup label={ t('Ciudad') }>
                                            <SelectSearch
                                                disabled={ !!citiesLoading }
                                                margin="0px 0px 20px 0px"
                                                options={ formatOptions(cities) }
                                                placeholder={ !!citiesLoading ? `${ t('Cargando') }...` : t('Ingrese una ciudad') }
                                                width="100%"
                                                { ...cityIdInput }
                                            />
                                        </FormGroup>
                                    </Col>
                                    {
                                        !isEmpty(districts) &&
                                            <Col md="6" sm="12">
                                                <FormGroup label={ t('Barrio') }>
                                                    <SelectSearch
                                                        disabled={ !!districtsLoading }
                                                        margin="0px 0px 20px 0px"
                                                        options={ formatOptions(districts, 'name', 'id') }
                                                        placeholder={ !!districtsLoading ? `${ t('Cargando') }...` : t('Ingrese un barrio') }
                                                        width="100%"
                                                        { ...districtIdInput }
                                                    />
                                                </FormGroup>
                                            </Col>
                                    }
                                </>
                        }
                    </Row>
                    <Row>
                        {
                            coveragesConfig &&
                                <>
                                    <Col md="4" sm="6" xs="12">
                                        <FormGroup label={ t('Cobertura') } >
                                            <CoveragePicker
                                                { ...medicalCoverageIdInput }
                                                margin="0px 0px 20px 0px"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4" sm="6" xs="12">
                                        <FormGroup label={ t('Plan') } >
                                            <PlanPicker
                                                params={ medicalCoverageId }
                                                { ...planIdInput }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4" sm="6" xs="12">
                                        <FormGroup label={ t('Afiliado') } >
                                            <Input
                                                { ...healthInsuranceInput }
                                            />
                                        </FormGroup>
                                    </Col>
                                </>
                        }
                    </Row>
                    <Row>
                        <Col sm="6" xs="12">
                            <FormGroup label={ t('Sexo') }>
                                <GenderPicker
                                    margin="0px 0px 20px 0px"
                                    value={ gender }
                                    onChange={ setGender }
                                />
                            </FormGroup>
                        </Col>
                        {
                            company &&
                            <Col sm="6" xs="12">
                                <FormGroup label={ t('Empresa') }>
                                    <CompanyPicker
                                        { ...companyInput }
                                        allowNone
                                        margin="0px 0px 20px 0px"
                                    />
                                </FormGroup>
                            </Col>
                        }
                        {
                            positions &&
                            <Col sm="6" xs="12">
                                <FormGroup label={ t('Cargo') }>
                                    <Container flexDirection="row" gap="10px" width="100%">
                                        <Container flex="1">
                                            <PositionPicker
                                                { ...positionInput }
                                                allowNone
                                                margin="0px 0px 20px 0px"
                                                width="auto"
                                            />
                                        </Container>
                                        {
                                            editMode &&
                                            <ExpandButton
                                                circle
                                                icon="open_in_new"
                                                size={ 40 }
                                                onClick={ () => setPositionModal(true) }
                                            />
                                        }
                                    </Container>
                                </FormGroup>
                            </Col>
                        }
                        {
                            workplaces &&
                            <Col sm="6" xs="12">
                                <FormGroup label={ t('Lugar de trabajo') }>
                                    <WorkplacePicker
                                        { ...workplaceInput }
                                        allowNone
                                        margin="0px 0px 20px 0px"
                                    />
                                </FormGroup>
                            </Col>
                        }
                        {
                            areas &&
                            <Col sm="6" xs="12">
                                <FormGroup label={ t('Zona') }>
                                    <AreaPicker
                                        { ...areaInput }
                                        allowNone
                                        margin="0px 0px 20px 0px"
                                    />
                                </FormGroup>
                            </Col>
                        }
                        {
                            costCenters &&
                            <Col sm="6" xs="12">
                                <FormGroup label={ t('Centro de costos') }>
                                    <CostCenterPicker
                                        { ...costCenterInput }
                                        allowNone
                                        margin="0px 0px 20px 0px"
                                    />
                                </FormGroup>
                            </Col>
                        }
                        {
                            recipes &&
                            <>
                                <Col sm="6" xs="12">
                                    <FormGroup label={ t('Responsable') } margin="0px 0px 20px 0px">
                                        <Container alignItems="center" flexDirection="row" gap="10px">
                                            <Container flex="1">
                                                <PatientPicker
                                                    { ...patientInChargeInput }
                                                    defaultSearch={ patientInChargeIdentification }
                                                    margin="0px"
                                                />
                                            </Container>
                                            <IconButton
                                                icon="close"
                                                size={ 20 }
                                                onClick={ () => setPatientInCharge(null) }
                                            />
                                            <ExpandButton
                                                circle
                                                icon="add"
                                                size={ 40 }
                                                onClick={ () => window.open(t('/pacientes/crear'), '_blank') }
                                            />
                                        </Container>
                                    </FormGroup>
                                </Col>
                                {
                                    !!patientInCharge &&
                                    <Col sm="6" xs="12">
                                        <FormGroup label={ t('Relación') } margin="0px 0px 20px 0px">
                                            <Container alignItems="center" flexDirection="row" gap="10px">
                                                <SelectSearch
                                                    options={ parseStringSelect(['parent', 'tutor'], parsePatientInChargeRelation) }
                                                    placeholder={ t('Sin relación') }
                                                    value={ patientInChargeRelation }
                                                    width="100%"
                                                    onChange={ value => setPatientInChargeRelation(value) }
                                                />
                                            </Container>
                                        </FormGroup>
                                    </Col>
                                }
                            </>
                        }
                    </Row>
                    <Row>
                        {
                            doctorsRelatedConfig &&
                                <>
                                    <Col sm="8" xs="12">
                                        <FormGroup label={ t('Profesionales asociados') } >
                                            <DoctorPicker
                                                objectChange
                                                allowNone={ false }
                                                filter={ ({ value }) => !doctorsRelated.find(({ value: relatedValue }) => value === relatedValue) }
                                                hasError={ !!errors.doctorsRelated }
                                                margin="0px"
                                                value={ doctor.id }
                                                onChange={ setDoctor }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <ResponsiveCol>
                                        <ExpandButton
                                            caption={ t('Agregar') }
                                            disabled={ !doctor.id }
                                            icon="add"
                                            margin="20px 0px"
                                            size={ 80 }
                                            width={ 100 }
                                            onClick={ handleDoctorAdd }
                                        />
                                    </ResponsiveCol>
                                    {
                                        !isEmpty(doctorsRelated) &&
                                        <Col xs="12">
                                            <TagList
                                                items={ doctorsRelated }
                                                margin="0px 0px 20px 0px"
                                                onRemove={ ({ value }) => removeDoctor('value', value) }
                                            />
                                        </Col>
                                    }
                                </>
                        }
                    </Row>
                    {
                        editMode && patientProfile &&
                        <Row>
                            <Col xs="12">
                                <Checkbox
                                    label={ t('Habilitado para ingresar a la plataforma') }
                                    margin="0px 0px 20px 0px"
                                    size={ 16 }
                                    { ...enabledCheckBox }
                                />
                            </Col>
                        </Row>
                    }
                    {
                        editMode && patientProfile && allowPatientChangePassword &&
                        <Row>
                            <Col xs="12">
                                <Checkbox
                                    label={ t('Forzar cambio de contraseña') }
                                    margin="0px 0px 20px 0px"
                                    size={ 16 }
                                    { ...mandatoryPasswordChangeChekBox }
                                />
                            </Col>
                        </Row>
                    }
                    {
                        editMode &&
                        <Row>
                            <Col sm="6" xs="12">
                                <FormGroup label={ t('Nueva contraseña') }>
                                    <Input
                                        error={ errors.password }
                                        { ...passwordInput }
                                    />
                                    { strongPasswordRequired && <PasswordSecurityCheck password={ password } /> }
                                </FormGroup>
                            </Col>
                            <Col sm="6" xs="12">
                                <FormGroup label={ t('Repetir contraseña') }>
                                    <Input
                                        error={ !!errors.password }
                                        { ...repeatPasswordInput }
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    }
                    {
                        !!deleted &&
                        <ErrorMessage
                            relative
                            error={ t('El paciente fue eliminado y no puede editarse') }
                            margin="0px 10px"
                        />
                    }
                    {
                        registrationError?.general &&
                        <ErrorMessage relative error={ registrationError?.general } margin="0px 10px" />
                    }
                    <ButtonFooter>
                        {
                            !deleted &&
                            <>
                                {
                                    editMode &&
                                    <ExpandButton
                                        danger
                                        caption={ t('Eliminar') }
                                        loading={ loading }
                                        margin="20px 0px"
                                        size={ 120 }
                                        onClick={ () => setDeleteConfirm(true) }
                                    />

                                }
                                <ExpandButton
                                    caption={ t('Confirmar') }
                                    loading={ loading }
                                    margin="20px 0px"
                                    size={ 120 }
                                    onClick={ () => setAcceptConfirm(true) }
                                />
                            </>
                        }
                    </ButtonFooter>
                </Container>
            </FormLayout>
        </>
    );

};

export default PatientRegistration;
